import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { deviceService } from '../../services/device.service';
import { Check, Loader2, Smartphone, Laptop, Tablet, Monitor, Gamepad, Watch, Package } from 'lucide-react';

interface DeviceSelectorProps {
  onSelect: (selection: {
    categoryId: number;
    brandId: number;
    modelId: number;
    customModelName?: string;
  }) => void;
  initialValues?: {
    categoryId?: number;
    brandId?: number;
    modelId?: number;
  };
}

const iconMap: Record<string, React.ElementType> = {
  smartphone: Smartphone,
  laptop: Laptop,
  tablet: Tablet,
  'smart-board': Monitor,
  'gaming-console': Gamepad,
  'smart-watch': Watch,
  other: Package,
};

export const DeviceSelector: React.FC<DeviceSelectorProps> = ({ onSelect, initialValues }) => {
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | undefined>(initialValues?.categoryId);
  const [selectedBrandId, setSelectedBrandId] = useState<number | undefined>(initialValues?.brandId);
  const [selectedModelId, setSelectedModelId] = useState<number | undefined>(initialValues?.modelId);
  const [customModelName, setCustomModelName] = useState('');
  const [isCustomModelSelected, setIsCustomModelSelected] = useState(false);

  // Fetch categories
  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ['device-categories'],
    queryFn: deviceService.getCategories,
  });

  // Fetch brands when category is selected
  const { data: brands, isLoading: brandsLoading } = useQuery({
    queryKey: ['device-brands', selectedCategoryId],
    queryFn: () => deviceService.getBrands(selectedCategoryId!),
    enabled: !!selectedCategoryId,
  });

  // Fetch models when brand is selected
  const { data: models, isLoading: modelsLoading } = useQuery({
    queryKey: ['device-models', selectedBrandId],
    queryFn: () => deviceService.getModels(selectedBrandId!),
    enabled: !!selectedBrandId && selectedBrandId !== -1,
  });

  const onSelectRef = useRef(onSelect);
  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    const trimmedCustomModel = customModelName.trim();
    const hasValidSelection = selectedCategoryId && selectedBrandId && (isCustomModelSelected ? !!trimmedCustomModel : !!selectedModelId);

    if (hasValidSelection) {
      onSelectRef.current({
        categoryId: selectedCategoryId,
        brandId: selectedBrandId,
        modelId: isCustomModelSelected ? 0 : selectedModelId!,
        customModelName: isCustomModelSelected ? trimmedCustomModel : undefined,
      });
    }
  }, [selectedCategoryId, selectedBrandId, selectedModelId, isCustomModelSelected, customModelName]);

  const handleCategorySelect = (categoryId: number) => {
    setSelectedCategoryId(categoryId);
    setSelectedBrandId(undefined);
    setSelectedModelId(undefined);
    setCustomModelName('');
    setIsCustomModelSelected(false);
  };

  const handleBrandSelect = (brandId: number) => {
    setSelectedBrandId(brandId);
    setSelectedModelId(undefined);
    setCustomModelName('');
    setIsCustomModelSelected(brandId === -1);
  };

  const handleModelSelect = (modelId: number) => {
    setSelectedModelId(modelId);
    setIsCustomModelSelected(false);
    setCustomModelName('');
  };

  const resetSelection = () => {
    setSelectedCategoryId(undefined);
    setSelectedBrandId(undefined);
    setSelectedModelId(undefined);
    setCustomModelName('');
    setIsCustomModelSelected(false);
  };

  if (categoriesLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-3 text-sm font-medium text-[rgb(var(--light-secondary))]">
          Step 1: Select Device Type
        </h3>
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
          {categories?.map((category) => {
            const Icon = iconMap[category.slug] || Package;
            const isSelected = selectedCategoryId === category.id;
            return (
              <button
                key={category.id}
                onClick={() => handleCategorySelect(category.id)}
                className={`flex flex-col items-center rounded-lg border p-3 transition-all ${
                  isSelected
                    ? 'border-primary bg-primary/10 shadow-sm'
                    : 'border-[rgb(var(--border))] hover:border-primary/50 hover:bg-[rgb(var(--card))]'
                }`}
              >
                <Icon className={`h-6 w-6 ${isSelected ? 'text-primary' : 'text-[rgb(var(--light-secondary))]'}`} />
                <span className={`mt-1 text-xs font-medium ${isSelected ? 'text-primary' : 'text-[rgb(var(--light-secondary))]'}`}>
                  {category.name}
                </span>
                {isSelected && <Check className="mt-1 h-3 w-3 text-primary" />}
              </button>
            );
          })}
        </div>
      </div>

      {selectedCategoryId && (
        <div>
          <h3 className="mb-3 text-sm font-medium text-[rgb(var(--light-secondary))]">
            Step 2: Select Brand
          </h3>
          {brandsLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {brands?.map((brand) => {
                const isSelected = selectedBrandId === brand.id;
                return (
                  <button
                    key={brand.id}
                    onClick={() => handleBrandSelect(brand.id)}
                    className={`rounded-full border px-4 py-2 text-sm transition-all ${
                      isSelected
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-[rgb(var(--border))] text-[rgb(var(--foreground))] hover:border-primary/50'
                    }`}
                  >
                    {brand.name}
                  </button>
                );
              })}
              <button
                onClick={() => handleBrandSelect(-1)}
                className={`rounded-full border px-4 py-2 text-sm transition-all ${
                  selectedBrandId === -1
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-[rgb(var(--border))] text-[rgb(var(--light-secondary))] hover:border-primary/50'
                }`}
              >
                Other
              </button>
            </div>
          )}
        </div>
      )}

      {selectedBrandId && (
        <div>
          <h3 className="mb-3 text-sm font-medium text-[rgb(var(--light-secondary))]">
            Step 3: Select Model
          </h3>
          {selectedBrandId !== -1 && modelsLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : (
            <>
              {selectedBrandId !== -1 && models && models.length > 0 && (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                  {models.map((model) => {
                    const isSelected = selectedModelId === model.id && !isCustomModelSelected;
                    return (
                      <button
                        key={model.id}
                        onClick={() => handleModelSelect(model.id)}
                        className={`rounded-lg border p-3 text-sm transition-all ${
                          isSelected
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-[rgb(var(--border))] text-[rgb(var(--foreground))] hover:border-primary/50'
                        }`}
                      >
                        {model.name}
                      </button>
                    );
                  })}
                </div>
              )}

              <button
                type="button"
                onClick={() => {
                  setSelectedModelId(undefined);
                  setIsCustomModelSelected(true);
                }}
                className={`mt-3 rounded-lg border border-dashed p-3 text-sm transition-all ${
                  isCustomModelSelected
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-[rgb(var(--border))] text-[rgb(var(--light-secondary))] hover:border-primary/50'
                }`}
              >
                My model isn’t listed
              </button>

              {isCustomModelSelected && (
                <div className="mt-4 rounded-lg border border-[rgb(var(--border))] bg-[rgb(var(--card))] p-4">
                  <label className="mb-2 block text-sm font-medium text-[rgb(var(--foreground))]">
                    Enter your phone model
                  </label>
                  <input
                    type="text"
                    value={customModelName}
                    onChange={(event) => setCustomModelName(event.target.value)}
                    placeholder="e.g. iPhone 15 Pro Max"
                    className="w-full rounded-lg border border-[rgb(var(--border))] bg-transparent px-3 py-2 text-sm outline-none ring-0 transition focus:border-primary"
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}

      {selectedCategoryId && (
        <button
          onClick={resetSelection}
          className="text-sm text-[rgb(var(--light-secondary))] hover:text-primary transition-colors"
        >
          ← Start over
        </button>
      )}
    </div>
  );
};